import { useAuth0 } from "@auth0/auth0-react";
import { useAllClients } from "../../Queries/ClientQueries";
import { useActivateUserQuerySelector, useCurrentUser } from "../../Queries/UserQueries";

export type CrmAccessStatus = "loading" | "error" | "noAccess" | "hasAccess";

/**
 * Resolves a single explicit CRM access status from the identity and clients queries'
 * settled/error state (not from a falsy `user.id`, which cannot distinguish "in flight"
 * from "settled with no id"). Reuses the existing query hooks so it shares the React Query
 * cache and adds no extra network requests.
 */
export function useCrmAccess(): CrmAccessStatus {
  const { user: auth0User } = useAuth0();
  const user = useCurrentUser();
  const userQuery = useActivateUserQuerySelector(auth0User ?? { email: "", nickname: "" });
  const clientsQuery = useAllClients(!!user.id);

  // identity
  if (!(userQuery.isSuccess || userQuery.isError)) return "loading"; // covers disabled(no token)+in-flight
  if (userQuery.isError) return "error"; // transient identity failure
  if (!user.id) return "noAccess"; // settled but no activateId claim

  // clients
  if (!(clientsQuery.isSuccess || clientsQuery.isError)) return "loading";
  if (clientsQuery.isError) {
    // A user with no CRM access is rejected by the backend with 401/403 on /api/clients
    // (not a 200 with an empty list). Since identity already resolved above, an auth
    // rejection here means "no CRM access"; other failures (network/5xx) are transient errors.
    const status = (clientsQuery.error as (Error & { status?: number }) | null)?.status;
    return status === 401 || status === 403 ? "noAccess" : "error";
  }
  return (clientsQuery.data?.length ?? 0) > 0 ? "hasAccess" : "noAccess";
}
